import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Overlay } from '@/components/Overlay';
import type { ConnectionDto, ProviderAvailability } from '@str/api/types';
import { PROVIDER_META, propertyChannels, type ProviderKey } from '@str/shared';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ChannelLogo } from '@/components/ChannelLogo';
import { useToast } from '@/components/toast';
import { ErrorText, Spinner } from '@/components/ui';
import { errorMessage } from '@/lib/api';
import { initials } from '@/lib/format';
import { useActiveProperty } from '@/lib/property-store';
import { channelsApi, keys, useChannels } from '@/lib/queries';

const ERRORS: Record<string, string> = {
  denied: 'You cancelled on the consent screen, so nothing was connected.',
  expired: 'That connection link expired. Start again from here.',
  no_accounts: 'No account we can publish to was found on that login.',
  exchange_failed: 'The platform did not accept the login. Try again.',
  provider_unavailable: 'That platform is not available in this environment.',
  missing_state: 'The platform sent us back without a state. Try again.',
  provider_mismatch: 'The platform sent us back to the wrong place. Try again.',
};

export function Channels() {
  const { active } = useActiveProperty();
  const channels = useChannels(active.id);
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const announced = useRef<string | null>(null);

  // The property's own tier, not the account subscription: tiers are per property.
  const allowed = propertyChannels(active.plan);
  const refresh = () => qc.invalidateQueries({ queryKey: keys.channels(active.id) });

  // The callback lands here with ?connected=, ?error= or ?select=. Errors render straight from
  // the URL; a pending pick is loaded by query; a fresh connection is announced once.
  const urlError = params.get('error');
  const urlDetail = params.get('detail');
  const selectState = params.get('select');
  const connectedId = params.get('connected');
  const error =
    actionError ??
    (urlError
      ? [ERRORS[urlError] ?? `Could not connect (${urlError}).`, urlDetail]
          .filter(Boolean)
          .join(' ')
      : null);
  const pending = useQuery({
    queryKey: ['channel-pick', selectState],
    queryFn: () => channelsApi.pending(selectState!),
    enabled: !!selectState,
    retry: false,
  });
  useEffect(() => {
    if (!connectedId || announced.current === connectedId) return;
    announced.current = connectedId;
    void refresh();
    toast('Connected');
    setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedId]);
  const clearUrl = () => setParams({}, { replace: true });
  const setError = (msg: string | null) => {
    setActionError(msg);
    if (!msg && urlError) clearUrl();
  };

  const connect = async (provider: ProviderKey) => {
    setError(null);
    setBusy(provider);
    try {
      const { url } = await channelsApi.connect(active.id, provider);
      window.location.assign(url);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(null);
    }
  };
  const disconnect = async (c: ConnectionDto) => {
    setError(null);
    setBusy(c.id);
    try {
      await channelsApi.disconnect(c.id);
      await refresh();
      toast(`Disconnected ${PROVIDER_META[c.provider].label}`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };
  const choose = async (externalId: string) => {
    if (!selectState) return;
    setBusy('pick');
    try {
      const conn = await channelsApi.select(selectState, externalId);
      clearUrl();
      await refresh();
      toast(`Connected ${conn.handle ? '@' + conn.handle.replace(/^@/, '') : conn.displayName}`);
    } catch (e) {
      setActionError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const data = channels.data;
  const anyMock = data?.providers.some((p) => p.mode === 'mock');

  return (
    <div className="a-rise" style={{ flex: 1, padding: '0 32px 32px', overflow: 'auto' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 16,
          margin: '22px 0 18px',
        }}
      >
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-.02em', margin: 0 }}>
            Channels
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13.5, color: '#666' }}>
            Where posts for {active.name} go. Connect an account once; every scheduled post that
            targets it is published there.
          </p>
        </div>
      </div>

      {anyMock && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            padding: '10px 14px',
            borderRadius: 12,
            background: 'var(--accent-tint)',
            color: 'var(--accent-dark)',
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          <span className="tag tag-sm" style={{ background: 'var(--accent-dark)' }}>
            Mock mode
          </span>
          Connecting shows a stand-in consent screen and publishing never leaves this machine. Real
          platforms switch on when their app credentials are configured.
        </div>
      )}
      <ErrorText>{error ?? (pending.error ? errorMessage(pending.error) : null)}</ErrorText>

      {channels.isPending || !data ? (
        <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
          <Spinner />
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 12,
          }}
        >
          {data.providers.map((p) => (
            <ProviderCard
              key={p.key}
              provider={p}
              connections={data.connections.filter((c) => c.provider === p.key)}
              locked={!allowed.includes(p.channel)}
              busy={busy}
              onConnect={() => void connect(p.key)}
              onDisconnect={(c) => void disconnect(c)}
              onUpgrade={() => void navigate('/app/settings')}
            />
          ))}
        </div>
      )}

      {selectState && pending.data && (
        <Overlay onClose={clearUrl}>
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Choose a ${pending.data.label} account`}
            className="card"
            style={{
              width: 420,
              maxWidth: 'calc(100% - 32px)',
              padding: 22,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
                Which {pending.data.label} account?
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>
                Your login manages more than one. Pick the one for {active.name}.
              </p>
            </div>
            {pending.data.accounts.map((a) => (
              <button
                key={a.externalId}
                type="button"
                className="hv"
                disabled={busy === 'pick'}
                onClick={() => void choose(a.externalId)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: 10,
                  borderRadius: 12,
                  border: '1px solid rgba(0,0,0,.1)',
                  background: '#fff',
                  textAlign: 'left',
                }}
              >
                <Avatar name={a.displayName} url={a.avatarUrl} />
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 14, fontWeight: 700 }}>{a.displayName}</span>
                  {a.handle && (
                    <span style={{ fontSize: 12, color: '#777' }}>
                      @{a.handle.replace(/^@/, '')}
                    </span>
                  )}
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 600 }}>Use →</span>
              </button>
            ))}
            <button type="button" className="btn btn-ghost hv" onClick={clearUrl}>
              Cancel
            </button>
          </div>
        </Overlay>
      )}
    </div>
  );
}

function Avatar({ name, url, size = 38 }: { name: string; url: string | null; size?: number }) {
  // Platform picture URLs expire (Meta's after a few weeks); a broken one falls back to initials.
  const [failed, setFailed] = useState<string | null>(null);
  return url && failed !== url ? (
    <img
      src={url}
      alt={name}
      width={size}
      height={size}
      onError={() => setFailed(url)}
      style={{ borderRadius: '50%', objectFit: 'cover', flex: 'none' }}
    />
  ) : (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'var(--brand-paper)',
        color: 'var(--brand-ink)',
        display: 'grid',
        placeItems: 'center',
        fontSize: 13,
        fontWeight: 800,
        flex: 'none',
      }}
    >
      {initials(name)}
    </span>
  );
}

/** The platform's everyday name, for the signed-out sentence. */
const PLATFORM_NAME: Record<ProviderKey, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  youtube: 'YouTube',
};

function ProviderCard({
  provider,
  connections,
  locked,
  busy,
  onConnect,
  onDisconnect,
  onUpgrade,
}: {
  provider: ProviderAvailability;
  connections: ConnectionDto[];
  locked: boolean;
  busy: string | null;
  onConnect: () => void;
  onDisconnect: (c: ConnectionDto) => void;
  onUpgrade: () => void;
}) {
  const meta = PROVIDER_META[provider.key];
  const connected = connections.length > 0;
  // A channel nobody can connect yet sells nothing: no Pro badge, no Upgrade, only the sentence.
  const selling = locked && !provider.unavailable;
  return (
    <section
      className="card"
      style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}
      aria-label={meta.label}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <ChannelLogo provider={provider.key} size={40} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <span style={{ fontSize: 15, fontWeight: 800 }}>{meta.label}</span>
          <span style={{ fontSize: 12, color: '#777' }}>{meta.blurb}</span>
        </div>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {provider.mode === 'mock' && (
            <span className="tag tag-sm" style={{ background: 'var(--accent-dark)' }}>
              Mock
            </span>
          )}
          {selling && (
            <span className="tag tag-sm" style={{ background: '#111' }}>
              Pro
            </span>
          )}
        </span>
      </div>

      {connections.map((c) => {
        const signedOut = c.status !== 'connected' || c.tokenValid === false;
        return (
          <div key={c.id} className="channel-row">
            <Avatar name={c.displayName} url={c.avatarUrl} size={34} />
            <div className="channel-row-text">
              <span style={{ fontSize: 13.5, fontWeight: 700 }}>{c.displayName}</span>
              <span style={{ fontSize: 12, color: signedOut ? '#a33a2a' : 'var(--success)' }}>
                {c.handle ? `@${c.handle.replace(/^@/, '')} · ` : ''}
                {signedOut ? 'Signed out · Reconnect' : 'Connected'}
              </span>
              {signedOut && (
                <span style={{ fontSize: 11.5, color: '#666', lineHeight: 1.4 }}>
                  {PLATFORM_NAME[c.provider]} signed this account out. Reconnect to keep publishing.
                </span>
              )}
            </div>
            <div className="channel-row-actions">
              {signedOut && (
                <button
                  type="button"
                  className="btn btn-primary hv"
                  style={{ fontSize: 12.5, padding: '7px 12px' }}
                  disabled={!!busy}
                  onClick={onConnect}
                >
                  Reconnect
                </button>
              )}
              <button
                type="button"
                className="btn btn-ghost hv"
                style={{ fontSize: 12.5, padding: '7px 12px' }}
                disabled={busy === c.id}
                onClick={() => onDisconnect(c)}
              >
                {busy === c.id ? 'Removing…' : 'Disconnect'}
              </button>
            </div>
          </div>
        );
      })}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 'auto' }}>
        {selling ? (
          <>
            <span style={{ fontSize: 12.5, color: '#777' }}>{meta.label} is included in Pro.</span>
            <button
              type="button"
              className="btn btn-outline hv"
              style={{ marginLeft: 'auto', fontSize: 12.5, padding: '8px 14px' }}
              onClick={onUpgrade}
            >
              Upgrade
            </button>
          </>
        ) : provider.unavailable ? (
          <span style={{ fontSize: 12.5, color: '#777' }}>{provider.unavailable}</span>
        ) : (
          <button
            type="button"
            className={`btn ${connected ? 'btn-outline' : 'btn-primary'} hv`}
            style={{ marginLeft: 'auto', fontSize: 13, padding: '9px 16px' }}
            disabled={busy === provider.key}
            onClick={onConnect}
          >
            {busy === provider.key
              ? 'Opening…'
              : connected
                ? 'Connect another'
                : `Connect ${meta.label}`}
          </button>
        )}
      </div>
      {/* Nothing to agree to while the host cannot connect; an existing login keeps its terms. */}
      {provider.key === 'youtube' && (connected || (!locked && !provider.unavailable)) && (
        <span style={{ fontSize: 11.5, color: '#777', lineHeight: 1.45 }}>
          STR Leads Social uses YouTube API Services: it uploads the videos you send here and reads
          their views, likes and comments. By connecting you agree to the{' '}
          <a href="https://www.youtube.com/t/terms" target="_blank" rel="noreferrer">
            YouTube Terms of Service
          </a>
          ; see also the{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
            Google Privacy Policy
          </a>
          . Disconnect removes our access and what we stored; your videos stay on YouTube. You can
          also remove access in your{' '}
          <a
            href="https://security.google.com/settings/security/permissions"
            target="_blank"
            rel="noreferrer"
          >
            Google security settings
          </a>
          .
        </span>
      )}
      {!connected && !locked && !provider.unavailable && (
        <span style={{ fontSize: 11.5, color: '#999', lineHeight: 1.4 }}>
          Posts that target {meta.label} are marked “not connected” until you do this.{' '}
          <Link to="/app/posts" className="tap-link">
            See posts
          </Link>
        </span>
      )}
    </section>
  );
}
